import {
    Breakpoint,
    ConfirmationDialog,
    JobShareDialog,
    mediator,
    MessageComposer,
    MessageRecipient,
    useRelodeAppUI,
    useRouter,
} from "@relode/core";
import { JobStatus } from "@relode/core/domain";
import * as React from "react";

import { TeamMember } from "../../../../../shared/types";
import { useJobContext } from "../../../_infra";
import { AddTeamMemberDialog } from "./addTeamMemberDialog";
import { SetAddTeamMemberSubmitStatus } from "./addTeamMemberDialogProps";
import { ChangeJobStatusDialog } from "./changeJobStatusDialog";
import { SetChangeJobStatusStatus } from "./changeJobStatusDialogProps";
import { JobDetailsDesktop } from "./jobDetailsDesktop";
import { JobDetailsMobile } from "./jobDetailsMobile";

export const JobDetails = () => {
    const {
        state: {
            data: { job, currentJobId, shareSuggestions, shareDetails },
        },
        actions,
    } = useJobContext();
    const { location } = useRouter();
    const [backUrl, setBackUrl] = React.useState("");

    const [addTeamMembersOpen, setAddTeamMembersOpen] = React.useState(false);
    const [
        confirmationModalIsOpen,
        setConfirmationModalIsOpen,
    ] = React.useState(false);
    const [
        selectedTeamMember,
        setSelectedTeamMember,
    ] = React.useState<TeamMember>(undefined);
    const [messageComposerOpen, setMessageComposerOpen] = React.useState(false);
    const [jobShareDialogOpen, setJobShareDialogOpen] = React.useState(false);
    const [initialRecipients, setInitialRecipients] = React.useState(
        [] as MessageRecipient[]
    );
    const { ui: appUIState } = useRelodeAppUI();
    const [openStatusModal, setOpenStatusModal] = React.useState<boolean>(
        false
    );
    const [nextJobStatus, setNextJobStatus] = React.useState<JobStatus>(
        undefined
    );

    const nextPossibleStatuses = React.useMemo(() => {
        const statuses: JobStatus[] = [];
        if (job.actions.canOpen) statuses.push(JobStatus.Open);
        if (job.actions.canPause) statuses.push(JobStatus.Paused);
        if (job.actions.canClose) statuses.push(JobStatus.Closed);
        return statuses;
    }, [job]);

    const {
        details: { canCopy, canEdit },
    } = job;

    React.useEffect(() => {
        if (!job) return;

        setBackUrl(location.state ? location.state["backUrl"] : undefined);
        appUIState.actions.setPageTitle(job.details.jobName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job]);

    const handleJobManagerMessageComposerOpen = () => {
        const recipient: MessageRecipient = {
            userId: job.details.jobManager.userId,
            displayName: job.details.jobManager.fullName,
            roleName: job.details.jobManager.roleName,
        };

        setInitialRecipients([recipient]);
        setMessageComposerOpen(true);
    };

    const handleCopyJob = () => {
        mediator.send("showAddJob", {
            jobId: currentJobId,
            isEditJob: false,
        });
    };

    const loadTeamMemberSuggestions = async (searchText: string) => {
        await actions.loadTeamMemberSuggestions(currentJobId, searchText);
    };

    const handleSubmitTeamMember = async (
        userId: number,
        clientJobUserTypeId: number,
        edit: boolean,
        setAddTeamMemberSubmitStatus: SetAddTeamMemberSubmitStatus
    ) => {
        if (edit) {
            await actions.updateTeamMember(
                currentJobId,
                userId,
                clientJobUserTypeId,
                setAddTeamMemberSubmitStatus
            );
        } else {
            await actions.addNewTeamMember(
                currentJobId,
                userId,
                clientJobUserTypeId,
                setAddTeamMemberSubmitStatus
            );
        }
    };
    const handleCompleteAddTeamMember = async () => {
        setAddTeamMembersOpen(false);
        setSelectedTeamMember(undefined);
        await actions.reloadJob();
    };

    const handleCloseAddTeamMember = () => {
        setAddTeamMembersOpen(false);
        setSelectedTeamMember(undefined);
    };

    const editTeamMember = (teamMember: TeamMember) => {
        setSelectedTeamMember(teamMember);
        setAddTeamMembersOpen(true);
    };
    const handleJobStatus = (nextStatus: JobStatus) => {
        setNextJobStatus(nextStatus);
        setOpenStatusModal(true);
    };

    const handleChangeJobStatus = async (
        nextStatus: JobStatus,
        comment: string,
        jobStatusChangedReasonTypeId?: number,
        setChangeJobStatusStatus?: SetChangeJobStatusStatus
    ) => {
        switch (nextStatus) {
            case JobStatus.Closed: {
                await actions.closeJob(
                    currentJobId,
                    comment,
                    jobStatusChangedReasonTypeId,
                    job.jobConcurrencyToken,
                    setChangeJobStatusStatus
                );
                break;
            }
            case JobStatus.Paused: {
                await actions.pauseJob(
                    currentJobId,
                    comment,
                    jobStatusChangedReasonTypeId,
                    job.jobConcurrencyToken,
                    setChangeJobStatusStatus
                );
                break;
            }
            case JobStatus.Open: {
                await actions.openJob(
                    currentJobId,
                    comment,
                    job.jobConcurrencyToken,
                    setChangeJobStatusStatus
                );
                break;
            }
        }
    };

    const handleComplete = () => {
        setOpenStatusModal(false);
        setNextJobStatus(undefined);
    };

    const handleShareJob = async () => {
        if (!currentJobId) return;

        await actions.loadShareLink(currentJobId);

        setJobShareDialogOpen(true);
    };

    const deleteTeamMember = (teamMember: TeamMember) => {
        setSelectedTeamMember(teamMember);
        setConfirmationModalIsOpen(true);
    };

    const handleConfirmDeleteTeamMember = async () => {
        const response = await actions.removeTeamMember(
            currentJobId,
            selectedTeamMember.userId
        );
        if (response === true) {
            await actions.reloadJob();
        }
        setSelectedTeamMember(undefined);
        setConfirmationModalIsOpen(false);
    };

    const handleReloadJob = async () => {
        await actions.reloadJob();
    };

    const handleEditJob = () => {
        mediator.send("showAddJob", {
            jobId: currentJobId,
            isEditJob: true,
            onComplete: handleReloadJob,
        });
    };

    return (
        <>
            <Breakpoint mobile>
                <JobDetailsMobile
                    backUrl={backUrl}
                    handleMessageComposer={handleJobManagerMessageComposerOpen}
                    handleOpenTeamMembersModal={() =>
                        setAddTeamMembersOpen(true)
                    }
                    handleEditTeamMember={editTeamMember}
                    handleDeleteTeamMember={deleteTeamMember}
                    handleShareJob={handleShareJob}
                    nextPossibleStatuses={nextPossibleStatuses}
                    handleChangeJobStatus={handleJobStatus}
                    handleCopyJob={canCopy ? handleCopyJob : null}
                    handleEditJob={canEdit ? handleEditJob : null}
                />
            </Breakpoint>
            <Breakpoint desktop>
                <JobDetailsDesktop
                    backUrl={backUrl}
                    handleMessageComposer={handleJobManagerMessageComposerOpen}
                    nextPossibleStatuses={nextPossibleStatuses}
                    handleChangeJobStatus={handleJobStatus}
                    handleOpenTeamMembersModal={() =>
                        setAddTeamMembersOpen(true)
                    }
                    handleEditTeamMember={editTeamMember}
                    handleDeleteTeamMember={deleteTeamMember}
                    handleShareJob={handleShareJob}
                    handleCopyJob={canCopy ? handleCopyJob : null}
                    handleEditJob={canEdit ? handleEditJob : null}
                />
            </Breakpoint>
            {messageComposerOpen ? (
                <MessageComposer
                    open={messageComposerOpen}
                    onClose={() => setMessageComposerOpen(false)}
                    jobName={job.details.jobName}
                    jobId={currentJobId}
                    initialRecipients={initialRecipients}
                    leadId={null}
                />
            ) : null}

            {jobShareDialogOpen ? (
                <JobShareDialog
                    onCancel={() => setJobShareDialogOpen(false)}
                    publicJobShareInfo={{
                        name: shareDetails.jobName,
                        emailUrl: shareDetails.emailUrl,
                        shortenedUrl: shareDetails.shortenedUrl,
                        facebookUrl: shareDetails.facebookUrl,
                        linkedInUrl: shareDetails.linkedInUrl,
                        twitterUrl: shareDetails.twitterUrl,
                    }}
                />
            ) : null}

            {addTeamMembersOpen ? (
                <AddTeamMemberDialog
                    jobName={job.details.jobName}
                    teamMember={selectedTeamMember}
                    suggestions={shareSuggestions}
                    onCancel={handleCloseAddTeamMember}
                    onComplete={handleCompleteAddTeamMember}
                    loadTeamMemberSuggestions={loadTeamMemberSuggestions}
                    onSubmit={handleSubmitTeamMember}
                />
            ) : null}

            {!!selectedTeamMember ? (
                <ConfirmationDialog
                    open={confirmationModalIsOpen}
                    title="remove team member"
                    // eslint-disable-next-line max-len
                    message={`Are you sure you want to remove ${selectedTeamMember.fullName} from the ${job.details.jobName} job?`}
                    onConfirm={handleConfirmDeleteTeamMember}
                    onClose={() => setConfirmationModalIsOpen(false)}
                />
            ) : null}

            {openStatusModal ? (
                <ChangeJobStatusDialog
                    jobName={job.details.jobName}
                    nextStatus={nextJobStatus}
                    onClose={() => setOpenStatusModal(false)}
                    onJobStatusChange={handleChangeJobStatus}
                    onComplete={handleComplete}
                />
            ) : null}
        </>
    );
};
